import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  paymentTransactionsData, settlementsData,
  filterTransactions, computeBillingSummary, computeBalance,
  priceCategories, getPriceCategoryConfig, getSettlementStatusConfig,
  formatEUR, getPatientName
} from "@/data/billingData";
import {
  Wallet, ArrowDownUp, Banknote, CreditCard, TrendingUp, TrendingDown,
  Receipt, Filter, FileDown, Calendar, CheckCircle2, AlertCircle, CircleDot, ListChecks
} from "lucide-react";

// Period presets relative to the latest transaction date in the dataset (so mock data is meaningful)
const PRESETS = [
  { id: "current_month", label: "Mese corrente" },
  { id: "last_month", label: "Mese precedente" },
  { id: "quarter", label: "Ultimo trimestre" },
  { id: "ytd", label: "Anno in corso" },
  { id: "all", label: "Tutto lo storico" },
  { id: "custom", label: "Range personalizzato" }
];

// Reference "today" for the mock dataset (latest transaction in seed = 2026-02-22)
// TODO: when backend persistence is wired, replace MOCK_TODAY with new Date() (or server-provided date).
const MOCK_TODAY = "2026-02-28";

const resolvePeriod = (presetId, customFrom, customTo) => {
  const today = MOCK_TODAY;
  const [y, m] = today.split("-").map(Number);
  const lastDay = (yy, mm) => new Date(yy, mm, 0).getDate();
  const pad = (n) => String(n).padStart(2, "0");
  switch (presetId) {
    case "current_month":
      return { from: `${y}-${pad(m)}-01`, to: `${y}-${pad(m)}-${lastDay(y, m)}` };
    case "last_month": {
      const ly = m === 1 ? y - 1 : y;
      const lm = m === 1 ? 12 : m - 1;
      return { from: `${ly}-${pad(lm)}-01`, to: `${ly}-${pad(lm)}-${lastDay(ly, lm)}` };
    }
    case "quarter": {
      const fromDate = new Date(y, m - 3, 1);
      return {
        from: `${fromDate.getFullYear()}-${pad(fromDate.getMonth() + 1)}-01`,
        to: `${y}-${pad(m)}-${lastDay(y, m)}`
      };
    }
    case "ytd":
      return { from: `${y}-01-01`, to: `${y}-${pad(m)}-${lastDay(y, m)}` };
    case "all":
      return { from: "2000-01-01", to: "2099-12-31" };
    case "custom":
      return { from: customFrom || "2000-01-01", to: customTo || "2099-12-31" };
    default:
      return { from: "2000-01-01", to: "2099-12-31" };
  }
};

const downloadCsv = (rows, columns, filename) => {
  const header = columns.map(c => c.label).join(",");
  const body = rows.map(r => columns.map(c => {
    const v = c.get ? c.get(r) : r[c.key];
    if (v === null || v === undefined) return "";
    const s = String(v).replace(/"/g, '""');
    return /[,;\n"]/.test(s) ? `"${s}"` : s;
  }).join(",")).join("\n");
  const blob = new Blob(["\ufeff" + header + "\n" + body], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

export const PharmacyBillingDrillDown = ({ pharmacyId, pharmacyName }) => {
  const [preset, setPreset] = useState("current_month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [filterMethod, setFilterMethod] = useState("all");
  const [settlements, setSettlements] = useState(settlementsData);
  const [closing, setClosing] = useState(null);
  const [closeNote, setCloseNote] = useState("");

  const { from, to } = resolvePeriod(preset, customFrom, customTo);

  const allTxs = useMemo(
    () => paymentTransactionsData.filter(t => t.pharmacyId === pharmacyId),
    [pharmacyId]
  );

  const periodTxs = useMemo(
    () => filterTransactions(allTxs, { from, to }),
    [allTxs, from, to]
  );

  const filteredTxs = useMemo(
    () => filterTransactions(periodTxs, { category: filterCat, paymentMethod: filterMethod }),
    [periodTxs, filterCat, filterMethod]
  );

  const summary = useMemo(() => computeBillingSummary(periodTxs), [periodTxs]);
  const balance = computeBalance(summary);

  const pharmacySettlements = useMemo(
    () => settlements.filter(s => s.pharmacyId === pharmacyId).sort((a, b) => b.period.localeCompare(a.period)),
    [settlements, pharmacyId]
  );

  const openSettlement = pharmacySettlements.find(s => s.status === "open");

  const handleClose = () => {
    if (!closing) return;
    setSettlements(prev => prev.map(s => s.id === closing.id ? {
      ...s,
      status: "settled",
      settledAt: new Date().toISOString().slice(0, 10),
      settledBy: "admin@witapp.it",
      note: closeNote || undefined
    } : s));
    toast.success("Conguaglio chiuso", { description: `Periodo ${closing.period} · saldo ${formatEUR(closing.balance)}` });
    setClosing(null);
    setCloseNote("");
  };

  const exportTxsCsv = () => {
    const cols = [
      { key: "date", label: "data" },
      { key: "id", label: "id_tx" },
      { key: "patient", label: "paziente", get: t => getPatientName(t.patientId) },
      { key: "serviceName", label: "prestazione" },
      { key: "category", label: "categoria", get: t => getPriceCategoryConfig(t.category).label },
      { key: "paymentMethod", label: "metodo", get: t => t.paymentMethod === "cash" ? "contanti" : "elettronico" },
      { key: "amount", label: "importo_totale" },
      { key: "quotaNewCo", label: "quota_newco" },
      { key: "quotaFarmacia", label: "quota_farmacia" },
      { key: "quotaStruttura", label: "quota_struttura" }
    ];
    downloadCsv(filteredTxs, cols, `transazioni_${pharmacyId}_${from}_${to}.csv`);
  };

  const exportSettlementsCsv = () => {
    const cols = [
      { key: "period", label: "periodo" },
      { key: "transactionCount", label: "n_transazioni" },
      { key: "totalValue", label: "totale_prestazioni" },
      { key: "totalElectronic", label: "incassato_newco" },
      { key: "totalCash", label: "incassato_cash" },
      { key: "quotaFarmaciaDue", label: "quota_farmacia_dovuta" },
      { key: "balance", label: "conguaglio" },
      { key: "status", label: "stato" },
      { key: "settledAt", label: "data_chiusura" }
    ];
    downloadCsv(pharmacySettlements, cols, `conguagli_${pharmacyId}.csv`);
  };

  return (
    <div className="space-y-6">
      {/* Period filter */}
      <Card data-testid="billing-period-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="h-4 w-4 text-violet-600" /> Periodo di analisi
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {PRESETS.map(p => (
              <button
                key={p.id}
                onClick={() => setPreset(p.id)}
                data-testid={`billing-preset-${p.id}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                  preset === p.id
                    ? "bg-violet-600 text-white border-violet-600"
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {preset === "custom" && (
            <div className="grid grid-cols-2 gap-3 max-w-md">
              <div>
                <Label className="text-xs">Dal</Label>
                <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} data-testid="billing-custom-from" />
              </div>
              <div>
                <Label className="text-xs">Al</Label>
                <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} data-testid="billing-custom-to" />
              </div>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Range attivo: <span className="font-mono">{from}</span> → <span className="font-mono">{to}</span> · {summary.count} transazioni
          </p>
        </CardContent>
      </Card>

      {/* KPI */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <Card data-testid="billing-kpi-total" className="border-2 bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Receipt className="h-5 w-5 text-emerald-700" />
            </div>
            <div>
              <p className="text-2xl font-bold leading-none">{formatEUR(summary.totalValue)}</p>
              <p className="text-[11px] mt-1 uppercase tracking-wider text-muted-foreground">Totale prestazioni</p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="billing-kpi-newco" className="border-2 bg-gradient-to-br from-violet-50 to-white">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-violet-100 flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-violet-700" />
            </div>
            <div>
              <p className="text-2xl font-bold leading-none">{formatEUR(summary.totalElectronic)}</p>
              <p className="text-[11px] mt-1 uppercase tracking-wider text-muted-foreground">Incassato NewCo (elettronico)</p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="billing-kpi-cash" className="border-2 bg-gradient-to-br from-sky-50 to-white">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-sky-100 flex items-center justify-center">
              <Banknote className="h-5 w-5 text-sky-700" />
            </div>
            <div>
              <p className="text-2xl font-bold leading-none">{formatEUR(summary.totalCash)}</p>
              <p className="text-[11px] mt-1 uppercase tracking-wider text-muted-foreground">Incassato Farmacia (contanti)</p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="billing-kpi-balance" className={`border-2 ${balance > 0 ? "bg-gradient-to-br from-rose-50 to-white" : balance < 0 ? "bg-gradient-to-br from-amber-50 to-white" : "bg-gradient-to-br from-slate-50 to-white"}`}>
          <CardContent className="p-4 flex items-center gap-3">
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${balance > 0 ? "bg-rose-100" : balance < 0 ? "bg-amber-100" : "bg-slate-100"}`}>
              {balance > 0 ? <TrendingUp className="h-5 w-5 text-rose-700" />
                : balance < 0 ? <TrendingDown className="h-5 w-5 text-amber-700" />
                : <ArrowDownUp className="h-5 w-5 text-slate-700" />}
            </div>
            <div>
              <p className={`text-2xl font-bold leading-none ${balance > 0 ? "text-rose-700" : balance < 0 ? "text-amber-700" : "text-slate-700"}`}>
                {balance > 0 ? "+" : ""}{formatEUR(balance)}
              </p>
              <p className="text-[11px] mt-1 uppercase tracking-wider text-muted-foreground">
                Conguaglio {balance > 0 ? "(→ NewCo)" : balance < 0 ? "(→ Farmacia)" : "(in pari)"}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Breakdown by category */}
      <Card data-testid="billing-breakdown-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Wallet className="h-4 w-4 text-indigo-600" /> Breakdown per categoria
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b">
                  <th className="p-2">Categoria</th>
                  <th className="p-2 text-right">#</th>
                  <th className="p-2 text-right">Valore</th>
                  <th className="p-2 text-right">Quota NewCo</th>
                  <th className="p-2 text-right">Quota Farmacia</th>
                  <th className="p-2 text-right">Quota Struttura</th>
                </tr>
              </thead>
              <tbody>
                {priceCategories.map(c => {
                  const row = summary.byCategory[c.id];
                  return (
                    <tr key={c.id} className="border-b last:border-0" data-testid={`billing-cat-row-${c.id}`}>
                      <td className="p-2">
                        <Badge variant="outline" className={c.color}>{c.label}</Badge>
                      </td>
                      <td className="p-2 text-right font-mono">{row.count}</td>
                      <td className="p-2 text-right font-mono font-semibold">{formatEUR(row.value)}</td>
                      <td className="p-2 text-right font-mono text-violet-700">{formatEUR(row.newco)}</td>
                      <td className="p-2 text-right font-mono text-indigo-700">{formatEUR(row.farmacia)}</td>
                      <td className="p-2 text-right font-mono text-cyan-700">{formatEUR(row.struttura)}</td>
                    </tr>
                  );
                })}
                <tr className="border-t-2 bg-muted/30 font-semibold">
                  <td className="p-2">Totale</td>
                  <td className="p-2 text-right font-mono">{summary.count}</td>
                  <td className="p-2 text-right font-mono">{formatEUR(summary.totalValue)}</td>
                  <td className="p-2 text-right font-mono text-violet-700">{formatEUR(summary.totalQuotaNewCo)}</td>
                  <td className="p-2 text-right font-mono text-indigo-700">{formatEUR(summary.totalQuotaFarmacia)}</td>
                  <td className="p-2 text-right font-mono text-cyan-700">{formatEUR(summary.totalQuotaStruttura)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Transactions */}
      <Card data-testid="billing-transactions-card">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-slate-600" /> Transazioni del periodo
            </CardTitle>
            <Button variant="outline" size="sm" onClick={exportTxsCsv} className="gap-1.5" data-testid="billing-export-tx-btn">
              <FileDown className="h-3.5 w-3.5" /> Esporta CSV
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Filter className="h-3.5 w-3.5 text-muted-foreground" />
            <select
              className="border rounded-md p-1.5 text-xs bg-background"
              value={filterCat}
              onChange={(e) => setFilterCat(e.target.value)}
              data-testid="billing-filter-category"
            >
              <option value="all">Tutte le categorie</option>
              {priceCategories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            <select
              className="border rounded-md p-1.5 text-xs bg-background"
              value={filterMethod}
              onChange={(e) => setFilterMethod(e.target.value)}
              data-testid="billing-filter-method"
            >
              <option value="all">Tutti i metodi</option>
              <option value="cash">Contanti</option>
              <option value="electronic">Elettronico</option>
            </select>
            <span className="text-[11px] text-muted-foreground ml-auto">{filteredTxs.length} di {periodTxs.length}</span>
          </div>
        </CardHeader>
        <CardContent>
          {filteredTxs.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-8">Nessuna transazione nel periodo/filtro selezionato.</div>
          ) : (
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b">
                    <th className="p-2">Data</th>
                    <th className="p-2">Paziente</th>
                    <th className="p-2">Prestazione</th>
                    <th className="p-2">Categoria</th>
                    <th className="p-2">Metodo</th>
                    <th className="p-2 text-right">Importo</th>
                    <th className="p-2 text-right">NewCo</th>
                    <th className="p-2 text-right">Farmacia</th>
                    <th className="p-2 text-right">Struttura</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTxs.map(t => {
                    const cat = getPriceCategoryConfig(t.category);
                    return (
                      <tr key={t.id} className="border-b last:border-0 hover:bg-muted/30" data-testid={`billing-tx-row-${t.id}`}>
                        <td className="p-2 font-mono">{t.date}</td>
                        <td className="p-2">{getPatientName(t.patientId)}</td>
                        <td className="p-2">{t.serviceName}</td>
                        <td className="p-2"><Badge variant="outline" className={`text-[10px] ${cat.color}`}>{cat.short}</Badge></td>
                        <td className="p-2">
                          <span className={`inline-flex items-center gap-1 text-[11px] ${t.paymentMethod === "cash" ? "text-sky-700" : "text-violet-700"}`}>
                            {t.paymentMethod === "cash" ? <Banknote className="h-3 w-3" /> : <CreditCard className="h-3 w-3" />}
                            {t.paymentMethod === "cash" ? "Contanti" : "Elettronico"}
                          </span>
                        </td>
                        <td className="p-2 text-right font-mono font-semibold">{formatEUR(t.amount)}</td>
                        <td className="p-2 text-right font-mono text-violet-700">{formatEUR(t.quotaNewCo)}</td>
                        <td className="p-2 text-right font-mono text-indigo-700">{formatEUR(t.quotaFarmacia)}</td>
                        <td className="p-2 text-right font-mono text-cyan-700">{formatEUR(t.quotaStruttura)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Settlements history */}
      <Card data-testid="billing-settlements-card">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <CircleDot className="h-4 w-4 text-amber-600" /> Storico conguagli
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                {pharmacySettlements.length} periodi · {openSettlement ? "1 aperto" : "tutti saldati"}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={exportSettlementsCsv} className="gap-1.5" data-testid="billing-export-settlements-btn">
              <FileDown className="h-3.5 w-3.5" /> Esporta CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {pharmacySettlements.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-8">Nessun conguaglio storicizzato.</div>
          ) : (
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b">
                    <th className="p-2">Periodo</th>
                    <th className="p-2 text-right">#</th>
                    <th className="p-2 text-right">Totale</th>
                    <th className="p-2 text-right">Cash</th>
                    <th className="p-2 text-right">NewCo</th>
                    <th className="p-2 text-right">Quota farm. dovuta</th>
                    <th className="p-2 text-right">Conguaglio</th>
                    <th className="p-2">Stato</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {pharmacySettlements.map(s => {
                    const st = getSettlementStatusConfig(s.status);
                    return (
                      <tr key={s.id} className="border-b last:border-0" data-testid={`billing-settlement-row-${s.id}`}>
                        <td className="p-2 font-mono font-semibold">{s.period}</td>
                        <td className="p-2 text-right font-mono">{s.transactionCount}</td>
                        <td className="p-2 text-right font-mono">{formatEUR(s.totalValue)}</td>
                        <td className="p-2 text-right font-mono text-sky-700">{formatEUR(s.totalCash)}</td>
                        <td className="p-2 text-right font-mono text-violet-700">{formatEUR(s.totalElectronic)}</td>
                        <td className="p-2 text-right font-mono text-indigo-700">{formatEUR(s.quotaFarmaciaDue)}</td>
                        <td className={`p-2 text-right font-mono font-semibold ${s.balance > 0 ? "text-rose-700" : s.balance < 0 ? "text-amber-700" : "text-slate-700"}`}>
                          {s.balance > 0 ? "+" : ""}{formatEUR(s.balance)}
                        </td>
                        <td className="p-2">
                          <Badge variant="outline" className={st.color}>
                            {s.status === "settled" ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <AlertCircle className="h-3 w-3 mr-1" />}
                            {st.label}
                          </Badge>
                          {s.settledAt && <p className="text-[9px] text-muted-foreground mt-0.5">il {s.settledAt}</p>}
                        </td>
                        <td className="p-2 text-right">
                          {s.status === "open" && (
                            <Button size="sm" variant="outline" onClick={() => setClosing(s)} data-testid={`billing-close-settlement-${s.id}`}>
                              Chiudi
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Close settlement dialog */}
      <Dialog open={!!closing} onOpenChange={(o) => !o && setClosing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Chiusura conguaglio</DialogTitle>
            <DialogDescription>
              Confermi la chiusura del periodo {closing?.period} per {pharmacyName}?
            </DialogDescription>
          </DialogHeader>
          {closing && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 p-3 rounded-md border bg-muted/30 text-xs">
                <div>Transazioni</div><div className="text-right font-mono">{closing.transactionCount}</div>
                <div>Totale prestazioni</div><div className="text-right font-mono">{formatEUR(closing.totalValue)}</div>
                <div>Cash incassato</div><div className="text-right font-mono">{formatEUR(closing.totalCash)}</div>
                <div>Quota farmacia dovuta</div><div className="text-right font-mono">{formatEUR(closing.quotaFarmaciaDue)}</div>
                <div className="font-semibold">Conguaglio</div>
                <div className={`text-right font-mono font-semibold ${closing.balance > 0 ? "text-rose-700" : closing.balance < 0 ? "text-amber-700" : ""}`}>
                  {closing.balance > 0 ? "+" : ""}{formatEUR(closing.balance)}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {closing.balance > 0
                  ? `La farmacia deve versare ${formatEUR(closing.balance)} a NewCo.`
                  : closing.balance < 0
                  ? `NewCo deve liquidare ${formatEUR(Math.abs(closing.balance))} alla farmacia.`
                  : "Nessun saldo da regolare."}
              </p>
              <div>
                <Label className="text-xs">Nota (opzionale)</Label>
                <Input value={closeNote} onChange={(e) => setCloseNote(e.target.value)} placeholder="Riferimento bonifico / cassa…" data-testid="billing-close-note" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setClosing(null)}>Annulla</Button>
            <Button onClick={handleClose} data-testid="billing-close-confirm-btn">Conferma chiusura</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PharmacyBillingDrillDown;
